/**
 * Template Processing Service
 * 
 * Handles processing of template results and filtering of fields
 */

/**
 * Process template results and filter fields based on output fields configuration
 * @param {Object} results - Raw template results
 * @param {Array} templates - Array of template configurations
 * @returns {Object} Filtered template results
 */
export const processTemplateResults = (results, templates) => {
  const filteredResults = {};
  
  // Process each product's results
  for (const [productId, templateResults] of Object.entries(results)) {
    filteredResults[productId] = {};
    
    // Process each template's results for this product
    for (const [templateId, templateResult] of Object.entries(templateResults)) {
      // Find the template configuration to get the outputFields
      const template = templates.find(t => t.id === templateId);
      
      if (!template) {
        console.warn(`Template with ID ${templateId} not found. Cannot filter fields.`);
        filteredResults[productId][templateId] = templateResult;
        continue;
      }
      
      console.log(`Processing template ${template.name} (ID: ${templateId}) for product ${productId}`);
      console.log(`Template output fields:`, template.outputFields);
      
      // Create a filtered result object for this template
      const filteredTemplateResult = {};
      
      // Process each field in the template result
      for (const [field, value] of Object.entries(templateResult)) {
        // Skip option values fields if they're not in the output fields
        if (field.startsWith('option') && field.endsWith('_values') && 
            !template.outputFields.some(f => f === field || f === 'options')) {
          console.log(`Excluding option values field "${field}" from filtered results (not in output fields)`);
          continue;
        }
        
        // Check if this field is in the template's outputFields
        const isOutputField = template.outputFields.some(outputField => {
          // Direct field match
          if (outputField === field) {
            return true;
          }
          
          // Handle option fields (option1_name, option1_values, etc.)
          if (field.startsWith('option') && field.includes('_')) {
            const optionBase = field.split('_')[0]; // e.g., "option1"
            if (outputField === optionBase || outputField === 'options') {
              return true;
            }
          }
          
          // Handle metafield notation
          if ((field.startsWith('metafield.') || field.startsWith('metafields.') || field.startsWith('custom.')) && 
              (outputField === 'metafields' || outputField === field)) {
            return true;
          }
          
          return false;
        });
        
        // Special case for options which should only be included when option fields are requested
        const isSpecialField = (field === 'options' && template.outputFields.some(f => 
                              f.startsWith('option') || f === 'options')) || 
                             (field === 'variants' && template.outputFields.some(f => 
                              f.startsWith('variants.')));
        
        if (isOutputField || isSpecialField) {
          console.log(`Including field "${field}" in filtered results for product ${productId}`);
          filteredTemplateResult[field] = value;
        } else {
          console.log(`Excluding field "${field}" from filtered results for product ${productId} (not in output fields)`);
        }
      }
      
      // Store the filtered result for this template
      filteredResults[productId][templateId] = filteredTemplateResult;
    }
  }
  
  console.log('Filtered template results:', filteredResults);
  return filteredResults;
};

/**
 * Prepare filtered product data with only the fields specified in inputFields
 * @param {Object} product - Product data
 * @param {Object} template - Template configuration
 * @param {Array} optionsToUse - Options to use for the product
 * @param {Function} getFieldValue - Function to get field value
 * @returns {Object} Filtered product data
 */
export const prepareFilteredProductData = (product, template, optionsToUse, getFieldValue) => {
  // Start with just the ID which is always needed
  const filteredData = {
    id: product.id
  };
  
  // Add only the fields specified in inputFields
  if (template.inputFields && Array.isArray(template.inputFields)) {
    template.inputFields.forEach(field => {
      // Handle basic fields
      if (field === 'title' || field === 'handle' || field === 'product_type' || 
          field === 'productType' || field === 'vendor' || field === 'tags') {
        filteredData[field] = getFieldValue(product, field);
      }
      
      // Handle option fields
      if (field.startsWith('option') && field.includes('_')) {
        const [optionBase, optionPart] = field.split('_');
        if (optionPart === 'name' || optionPart === 'values') {
          // If we need any option data, ensure the options array exists
          if (!filteredData.options) {
            filteredData.options = [];
          }
          
          // Extract option index (1-based in field name, 0-based in array)
          const optionIndex = parseInt(optionBase.replace('option', '')) - 1;
          
          // Get the option data
          if (optionsToUse && optionsToUse[optionIndex]) {
            // Ensure this option position exists in our array
            while (filteredData.options.length <= optionIndex) {
              filteredData.options.push({});
            }
            
            if (optionPart === 'name') {
              filteredData[field] = optionsToUse[optionIndex].name;
              filteredData.options[optionIndex].name = optionsToUse[optionIndex].name;
            } else if (optionPart === 'values') {
              filteredData[field] = optionsToUse[optionIndex].values;
              filteredData.options[optionIndex].values = optionsToUse[optionIndex].values;
            }
          }
        }
      }
      
      // Handle metafield notation
      if (field.includes('.')) {
        const [namespace, key] = field.split('.');
        
        // Get metafield value
        const metafieldValue = getFieldValue(product, field);
        if (metafieldValue !== undefined) {
          filteredData[field] = metafieldValue;
        }
      }
    });
  }
  
  return filteredData;
};

/**
 * Process template result to handle options and variants
 * @param {Object} result - Template result
 * @param {Object} template - Template configuration
 * @param {Object} filteredProductData - Filtered product data
 * @returns {Object} Processed result
 */
export const processTemplateResult = (result, template, filteredProductData) => {
  // Only initialize options when option fields are requested
  if (template.outputFields?.some(f => f.startsWith('option') || f === 'options')) {
    // Process option names and values
    if (result.option1_name !== undefined && result.option1_name !== "undefined" && result.option1_name !== "null" && filteredProductData.option1_name) {
      console.log(`Template provided option1_name: "${result.option1_name}"`);
      
      // Ensure options array exists
      if (!result.options) {
        result.options = [];
      }
      
      // Ensure option1 exists
      if (!result.options[0]) {
        result.options[0] = {};
      }
      
      // Set option1 name
      result.options[0].name = result.option1_name;
      
      // Log what's being set
      console.log(`Setting option1 name to: "${result.option1_name}"`);
    } else if (filteredProductData.option1_name) {
      // If template didn't provide a name but we have one from the product data, preserve it
      console.log(`Preserving original option1_name: "${filteredProductData.option1_name}"`);
      
      if (!result.options) {
        result.options = [];
      }
      
      if (!result.options[0]) {
        result.options[0] = {};
      }
      
      // Preserve the original name
      result.option1_name = filteredProductData.option1_name;
      result.options[0].name = filteredProductData.option1_name;
    }
    
    if (result.option2_name !== undefined && result.option2_name !== "undefined" && result.option2_name !== "null" && filteredProductData.option2_name) {
      console.log(`Template provided option2_name: "${result.option2_name}"`);
      
      // Ensure options array exists
      if (!result.options) {
        result.options = [];
      }
      
      // Ensure option2 exists
      if (!result.options[1]) {
        result.options[1] = {};
      }
      
      // Set option2 name
      result.options[1].name = result.option2_name;
      
      // Log what's being set
      console.log(`Setting option2 name to: "${result.option2_name}"`);
    } else if (filteredProductData.option2_name) {
      // If template didn't provide a name but we have one from the product data, preserve it
      console.log(`Preserving original option2_name: "${filteredProductData.option2_name}"`);
      
      if (!result.options) {
        result.options = [];
      }
      
      if (!result.options[1]) {
        result.options[1] = {};
      }
      
      // Preserve the original name
      result.option2_name = filteredProductData.option2_name;
      result.options[1].name = filteredProductData.option2_name;
    }
    
    if (result.option3_name !== undefined && result.option3_name !== "undefined" && result.option3_name !== "null" && filteredProductData.option3_name) {
      console.log(`Template provided option3_name: "${result.option3_name}"`);
      
      // Ensure options array exists
      if (!result.options) {
        result.options = [];
      }
      
      // Ensure option3 exists
      if (!result.options[2]) {
        result.options[2] = {};
      }
      
      // Set option3 name
      result.options[2].name = result.option3_name;
      
      // Log what's being set
      console.log(`Setting option3 name to: "${result.option3_name}"`);
    } else if (filteredProductData.option3_name) {
      // If template didn't provide a name but we have one from the product data, preserve it
      console.log(`Preserving original option3_name: "${filteredProductData.option3_name}"`);
      
      if (!result.options) {
        result.options = [];
      }
      
      if (!result.options[2]) {
        result.options[2] = {};
      }
      
      // Preserve the original name
      result.option3_name = filteredProductData.option3_name;
      result.options[2].name = filteredProductData.option3_name;
    }
  } else {
    // If no option fields are requested, ensure options are not included
    delete result.options;
    delete result.option1_name;
    delete result.option1_values;
    delete result.option2_name;
    delete result.option2_values;
    delete result.option3_name;
    delete result.option3_values;
  }

  // Process option values only if they're in output fields
  if (template.outputFields?.some(f => f === 'option1_values' || f === 'options')) {
    // Process option1_values if present and requested
    if (result.option1_values) {
      console.log(`Template result contains option1_values:`, result.option1_values);
      
      // Ensure options array exists
      if (!result.options) {
        result.options = [];
      }
      
      // Ensure option1 exists
      if (!result.options[0]) {
        result.options[0] = {};
      }
      
      // Set option1 values - use the values from this template only
      result.options[0].values = result.option1_values;
      
      // Log the values being set
      console.log(`Setting option1 values to:`, result.options[0].values);
    }
  }

  if (template.outputFields?.some(f => f === 'option2_values' || f === 'options')) {
    // Process option2_values if present and requested
    if (result.option2_values) {
      console.log(`Template result contains option2_values:`, result.option2_values);
      
      // Ensure options array exists
      if (!result.options) {
        result.options = [];
      }
      
      // Ensure option2 exists
      if (!result.options[1]) {
        result.options[1] = {};
      }
      
      // Set option2 values - use the values from this template only
      result.options[1].values = result.option2_values;
      
      // Log the values being set
      console.log(`Setting option2 values to:`, result.options[1].values);
    }
  }

  if (template.outputFields?.some(f => f === 'option3_values' || f === 'options')) {
    // Process option3_values if present and requested
    if (result.option3_values) {
      console.log(`Template result contains option3_values:`, result.option3_values);
      
      // Ensure options array exists
      if (!result.options) {
        result.options = [];
      }
      
      // Ensure option3 exists
      if (!result.options[2]) {
        result.options[2] = {};
      }
      
      // Set option3 values - use the values from this template only
      result.options[2].values = result.option3_values;
      
      // Log the values being set
      console.log(`Setting option3 values to:`, result.options[2].values);
    }
  }

  // Log the alt_text field specifically to help with debugging
  if (result.alt_text) {
    console.log(`Template result contains alt_text: ${result.alt_text}`);
    
    // Map alt_text to image_alt_text in results
    result.image_alt_text = result.alt_text;
  }

  // Log the body_html field specifically to help with debugging
  if (result.body_html) {
    console.log(`Template result contains body_html: ${result.body_html}`);
    
    // Map body_html to descriptionHtml in results
    result.descriptionHtml = result.body_html;
  }

  return result;
};
